// <!-- glow-menopause-style-enhancements -->
import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const ThyroidLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    window.location.href = '/book';
  };

  const painPoints = [
    {
      title: 'Exhaustion that sleep cannot touch',
      pain: 'Are you sleeping eight hours and still dragging yourself out of bed, hitting a wall by 2pm, and wondering when you started running on empty?',
      description: 'Bone-deep fatigue that does not improve with rest is one of the loudest signs of an underactive thyroid. It is not laziness. It is biology, and it is treatable.'
    },
    {
      title: 'Weight that will not move',
      pain: 'Are you eating clean, hitting the gym, doing everything "right", and watching the scale stay stuck or creep up anyway?',
      description: 'Your thyroid is the master regulator of your metabolism. When it is even slightly off, weight loss becomes nearly impossible regardless of effort. The problem is not your willpower.'
    },
    {
      title: 'Cold to your bones',
      pain: 'Are you the one in the office wearing a sweater in July, with hands and feet that never warm up no matter how many blankets you pile on?',
      description: 'Persistent cold intolerance is a classic hypothyroid symptom and is too often dismissed as "just being a cold person." It is your metabolism waving a red flag.'
    },
    {
      title: 'Hair you used to recognize',
      pain: 'Are you finding clumps in the shower, watching your eyebrows thin from the outer edge in, or staring at a ponytail that is half what it used to be?',
      description: 'Diffuse hair loss and thinning of the outer third of your eyebrows are hallmark thyroid signs. Most providers do not even ask. We do.'
    },
    {
      title: 'A brain that is not yours',
      pain: 'Are you walking into rooms and forgetting why, losing words mid-sentence, or rereading the same paragraph four times?',
      description: 'Thyroid hormone runs your brain. When it is low, processing slows, memory dips, and motivation evaporates. This is not "just stress." It is hormonal, and it lifts with the right treatment.'
    },
    {
      title: 'Doctors who told you "labs look fine"',
      pain: 'Have you been told your TSH is "normal" while you feel anything but, or been handed an antidepressant for what is clearly a physical problem?',
      description: 'Standard thyroid panels miss most thyroid problems. We test the full panel, TSH, free T3, free T4, reverse T3, and antibodies, because "normal" labs do not mean a normal life.'
    }
  ];

  const emotionalImpact = [
    {
      area: 'Your energy',
      impact: 'Coffee that no longer works, a 2pm crash you plan your day around, weekends spent recovering instead of living'
    },
    {
      area: 'Your body',
      impact: 'A reflection that feels like a stranger, clothes that no longer fit, hair and skin you barely recognize'
    },
    {
      area: 'Your mind',
      impact: 'A career that feels harder than it should, conversations you struggle to follow, a sharpness you used to take for granted'
    },
    {
      area: 'Your spirit',
      impact: 'Mood that has flatlined, motivation that has vanished, and the quiet grief of wondering if this is just who you are now'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Tell us how you actually feel',
      description: 'Share the symptoms, the patterns, and the years of being told it is "all in your head." We listen closely, with zero dismissal.'
    },
    {
      number: '2',
      title: 'Get a complete thyroid panel',
      description: 'TSH, free T3, free T4, reverse T3, TPO antibodies, and TG antibodies. Plus the metabolic markers most providers skip. Real data, not a guess.'
    },
    {
      number: '3',
      title: 'Receive a personalized plan',
      description: 'Targeted thyroid medication when needed (T4, T3, or natural desiccated), nutrient repletion, autoimmune support, and lifestyle protocols built for you.'
    },
    {
      number: '4',
      title: 'Get your energy back, then keep it',
      description: 'Frequent recheck labs, dose adjustments, and a clinician who optimizes how you FEEL, not just where your numbers fall on a generic chart.'
    }
  ];

  const proofPoints = [
    {
      stat: '1 in 8',
      title: 'Women develop thyroid disease',
      description: 'Thyroid disorders are far more common in women, especially after pregnancy or in perimenopause. You are not rare. You are just rarely tested correctly.'
    },
    {
      stat: '60%',
      title: 'Go undiagnosed',
      description: 'Most thyroid problems are missed because providers only run a TSH. A full panel catches what the basic test misses, every time.'
    },
    {
      stat: '4-8 wks',
      title: 'To start feeling different',
      description: 'With the right thyroid plan, most women report meaningful improvements in energy, mood, and weight within weeks, not years.'
    }
  ];

  const faqs = [
    {
      question: 'My doctor said my TSH is "normal." Why do I still feel terrible?',
      answer: 'TSH alone is not a complete picture. Many women feel awful with a "normal" TSH because their free T3, free T4, or reverse T3 is off, or they have thyroid antibodies indicating Hashimoto\'s. The standard reference range is also extremely wide, being in range does not mean being optimal. We use full-panel testing and treat how you feel, not just where you fall on a chart.'
    },
    {
      question: 'What is the difference between hypothyroidism and Hashimoto\'s?',
      answer: 'Hashimoto\'s is the autoimmune cause of most hypothyroidism. Your immune system slowly damages your thyroid, which is why you can have antibodies long before your TSH ever changes. Hashimoto\'s requires a different approach than just thyroid medication, we address the autoimmunity itself.'
    },
    {
      question: 'Will I have to take thyroid medication forever?',
      answer: 'For most women with true hypothyroidism, yes, but the right medication at the right dose feels nothing like "being on a drug." It feels like having yourself back. For some patients, especially those with Hashimoto\'s caught early, lifestyle and autoimmune protocols can reduce or eliminate the need for medication.'
    },
    {
      question: 'I have tried thyroid medication and it did not help. What is different here?',
      answer: 'Most likely you were prescribed a single form of thyroid hormone (T4) at a population-average dose with no follow-up labs. Many women need T3 too, or natural desiccated thyroid, or a different dose entirely. We tailor the medication, recheck regularly, and adjust until you actually feel well.'
    },
    {
      question: 'Can thyroid problems start after having a baby?',
      answer: 'Absolutely. Postpartum thyroiditis is extraordinarily common and routinely missed. If you have felt off since giving birth, exhausted, foggy, anxious, gaining weight, losing hair, your thyroid deserves a thorough look, not a "normal TSH" dismissal.'
    }
  ];

  return (
    <div className="pcos-page">
      {/* Header */}
      <header className="pcos-header">
        <div className="pcos-container pcos-header-inner">
          <a href="/" className="pcos-brand" data-testid="thyroid-brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="pcos-header-actions">
            <ServicesDropdown testIdPrefix="thyroid-nav-services" />
            <button className="pcos-btn pcos-btn-primary" onClick={scrollToBooking} data-testid="thyroid-header-cta">
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pcos-hero">
          <div className="pcos-container pcos-hero-split">
            <div className="pcos-hero-content">
              <span className="pcos-eyebrow">Thyroid Support</span>
              <h1 className="pcos-hero-headline">
                You are not lazy. You are not "just tired."<br />
                <span className="pcos-highlight">Your thyroid is asking for help.</span>
              </h1>
              <p className="pcos-hero-sub">
                If you are sleeping enough and still exhausted, eating clean and still gaining, doing everything right and still wondering what is wrong with you, you are not the problem. You have been given an incomplete test and a generic answer. We do thyroid differently.
              </p>
              <div className="pcos-hero-actions">
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="thyroid-hero-cta">
                  Get Real Answers
                  <ArrowRight size={18} />
                </button>
                <a href="#pain" className="pcos-btn pcos-btn-secondary" data-testid="thyroid-hero-secondary-cta">See If This Sounds Like You</a>
              </div>
              <p className="pcos-hero-note">Free consultation. Full thyroid panel. Finally, someone who actually looks.</p>
              <div className="meno-hero-credentials" data-testid="thyroid-hero-credentials">
                <p><strong>Utah-licensed clinician</strong> experienced in thyroid hormone care.</p>
                <p>Serving women in Utah via secure HIPAA video visits.</p>
              </div>
            </div>
            <div className="pcos-hero-image">
              <img
                src="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=640&h=760&fit=crop"
                alt="A woman who finally got the care she needed"
                loading="lazy"
              />
              <div className="pcos-hero-image-badge">
                "My energy came back."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* Three-panel journey strip */}
        <section className="meno-journey" data-testid="thyroid-journey">
          <div className="pcos-container">
            <div className="meno-journey-grid">
              <figure className="meno-journey-panel" data-testid="thyroid-journey-panel-1">
                <div className="meno-journey-image">
                  <img src="/thyroid-panel-1.png" alt="A woman foggy and tired at her kitchen counter with a symptom journal" loading="lazy" />
                </div>
                <figcaption>You are not lazy. Your thyroid is.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="thyroid-journey-panel-2">
                <div className="meno-journey-image">
                  <img src="/thyroid-panel-2.png" alt="A woman on a telehealth visit reviewing thyroid labs with a clinician" loading="lazy" />
                </div>
                <figcaption>Talk with a clinician who reads thyroid labs the right way.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="thyroid-journey-panel-3">
                <div className="meno-journey-image">
                  <img src="/thyroid-panel-3.png" alt="A woman walking briskly with restored energy and clarity" loading="lazy" />
                </div>
                <figcaption>A plan that gives you back your energy and your focus.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Specialist value prop */}
        <section className="meno-specialist" data-testid="thyroid-specialist">
          <div className="pcos-container">
            <p><strong>Not a general telehealth clinic.</strong> We specialize in thyroid and metabolic hormones, so your symptoms are not minimized as &quot;just stress&quot; or &quot;just aging.&quot;</p>
          </div>
        </section>


        {/* Pain Points */}
        <section className="pcos-section pcos-pain-section" id="pain">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Does This Sound Like You?</span>
              <h2>The thyroid symptoms most providers never connect</h2>
              <p>Your thyroid runs your metabolism, your energy, your hair, your mood, your weight, and your brain. When it is off, everything feels off, and "your TSH is fine" is rarely the full story.</p>
            </div>
            <div className="pcos-pain-grid">
              {painPoints.map((point, index) => (
                <div key={index} className="pcos-pain-card" data-testid={`thyroid-pain-card-${index}`}>
                  <h3>{point.title}</h3>
                  <p className="pcos-pain-question">{point.pain}</p>
                  <p className="pcos-pain-explain">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional Impact */}
        <section className="pcos-section pcos-impact-section">
          <div className="pcos-container">
            <div className="pcos-impact-wrap">
              <div className="pcos-impact-content">
                <span className="pcos-eyebrow">The Hidden Cost</span>
                <h2>Thyroid problems do not just slow your metabolism. They slow your entire life.</h2>
                <p className="pcos-impact-intro">
                  The labs are one thing. What it actually steals from you is something else.
                </p>
              </div>
              <div className="pcos-impact-grid">
                {emotionalImpact.map((item, index) => (
                  <div key={index} className="pcos-impact-card" data-testid={`thyroid-impact-card-${index}`}>
                    <h4>{item.area}</h4>
                    <p>{item.impact}</p>
                  </div>
                ))}
              </div>
              <p className="pcos-impact-cta">
                <strong>This is not "just aging." This is a fixable medical problem hiding inside a normal-looking lab.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="pcos-section pcos-quote-section" style={{backgroundImage: "url('/quote-bg-thyroid.png')"}}>
          <div className="pcos-container">
            <div className="pcos-quote-wrap">
              <blockquote>
                <p>"Three doctors told me my labs were fine. I felt like I was disappearing. The first full thyroid panel showed Hashimoto's. Six weeks into the right plan, I had myself back."</p>
                <cite>Real patient story</cite>
              </blockquote>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="pcos-section" id="plan">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">How We Help</span>
              <h2>A thorough approach to thyroid care</h2>
              <p>No more "your TSH is fine." No more guessing. No more being handed an antidepressant for what is clearly a physical problem.</p>
            </div>
            <div className="pcos-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="pcos-step-card" data-testid={`thyroid-step-card-${index}`}>
                  <div className="pcos-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
            <div className="pcos-steps-cta">
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="thyroid-steps-cta">
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <p className="pcos-cta-note">No commitment. No pressure. Just real testing.</p>
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="pcos-section pcos-proof-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">The Reality</span>
              <h2>You are not alone, even though it feels that way</h2>
            </div>
            <div className="pcos-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="pcos-proof-card" data-testid={`thyroid-proof-card-${index}`}>
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* Who this is right for */}
        <section className="pcos-section meno-fit-section" data-testid="thyroid-fit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">A Quick Honest Check</span>
              <h2>Who My Glow Health is right for</h2>
              <p>We are not for everyone, and that is the point. Here is who tends to feel the biggest shift working with us.</p>
            </div>
            <div className="meno-fit-grid">
              <div className="meno-fit-card meno-fit-yes" data-testid="thyroid-fit-yes">
                <h3>This is right for you if</h3>
                <ul>
                  <li>You live in Utah and want secure telehealth visits.</li>
                  <li>Your &quot;normal&quot; thyroid labs do not match how you actually feel.</li>
                  <li>You want a clinician who reviews Free T3, Free T4, and antibodies, not just TSH.</li>
                  <li>You are open to evidence-based thyroid hormone therapy if it is right for you.</li>
                  <li>You want a plan tailored to your labs and your symptoms, not a template.</li>
                </ul>
              </div>
              <div className="meno-fit-card meno-fit-no" data-testid="thyroid-fit-no">
                <h3>This is probably not for you if</h3>
                <ul>
                  <li>You want a quick prescription with no labs or follow-up.</li>
                  <li>You are not interested in lab-guided thyroid optimization.</li>
                  <li>You prefer a generalist who treats only the TSH number.</li>
                  <li>You prefer a one-size-fits-all wellness program.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="pcos-section pcos-cta-section" id="book">
          <div className="pcos-container">
            <div className="pcos-cta-wrap">
              <div className="pcos-cta-content">
                <span className="pcos-eyebrow">Take The First Step</span>
                <h2>Stop running on a thyroid no one is treating</h2>
                <ul className="pcos-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Get the FULL thyroid panel, not just a TSH</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Work with a clinician who knows the difference between "in range" and "optimal"</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get a treatment plan tailored to YOUR labs and YOUR symptoms</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Stop being told this is just stress, age, or motherhood</span>
                  </li>
                </ul>
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="thyroid-mid-cta">
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="pcos-cta-note">Free & confidential. Limited spots available each week.</p>
              </div>
              <div className="pcos-cta-visual">
                <div className="pcos-guarantee-card">
                  <h3>Our Promise</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will keep working with you for free until you do. No questions asked.</p>
                </div>
                <div className="pcos-testimonial-card">
                  <p>"For the first time in five years I have energy at 4pm. I almost forgot what that felt like."</p>
                  <span>Thyroid patient, age 41</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pcos-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Questions</span>
              <h2>What you are probably wondering</h2>
            </div>
            <div className="pcos-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`pcos-faq-item ${openFaq === index ? 'open' : ''}`}
                  data-testid={`thyroid-faq-item-${index}`}
                >
                  <button
                    className="pcos-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    data-testid={`thyroid-faq-toggle-${index}`}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="pcos-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="pcos-section pcos-final-cta">
          <div className="pcos-container">
            <div className="pcos-final-cta-content">
              <h2>You have been tired long enough</h2>
              <p>
                Every day you wait is another day of pretending. Another afternoon you push through. Another year of being told you are fine when you know you are not. A 15-minute conversation could be the moment everything starts turning around.
              </p>
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="thyroid-final-cta">
                Book Your Free Consultation Now
                <ArrowRight size={18} />
              </button>
              <p className="pcos-final-note">15 minutes could change how you feel for the next decade.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pcos-footer">
        <div className="pcos-container pcos-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="pcos-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="thyroid" />
    </div>
  );
};

export default ThyroidLanding;
